import React, { useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Modal as AntModal } from 'antd';
import produce from 'immer';
import { TableMainText, TableSecondaryText } from 'styles';

import { DEFAULT_PAGE_SIZE } from 'appConstants';
import { TableActions, TableTimestamp } from 'components';
import CustomIcon from 'components/Icons/Icon';

import BDOSettingAddEditModal from '../BDOSettingAddEditModal';
import BDOSettingsCardComponents from '../BDOSettingsCardComponents';
import { clearBDOSettings, deleteBDOSetting, getBDOSettings } from '../action';
import { ICreditCardReducerState } from '../types';

const { confirm } = AntModal;

const CashCreditLists = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCashCredit, setSelectedCashCredit] = useState<string | null>(null);

  const { bdoSettings, loading } = useSelector((state: any) => state.bdoSettings);

  const onCloseModal = () => {
    dispatch(clearBDOSettings());
    setIsEdit(false);
    setModalOpen(false);
    setSelectedCashCredit(null);
  };

  const paramsReducer = (state: ICreditCardReducerState, action): ICreditCardReducerState =>
    produce(state, draft => {
      switch (action.type) {
        case 'SEARCH':
          draft.q = action.value;
          draft.page = 1;
          break;
        case 'PAGINATION':
          draft.page = action.current;
          if (action.size) {
            draft.size = action.size;
          }
          break;
        default:
          break;
      }
    });

  const [params, paramsDispatch] = useReducer(paramsReducer, {
    q: null,
    size: DEFAULT_PAGE_SIZE,
    page: 1,
    type: 'cash_credit'
  });

  useEffect(() => {
    dispatch(getBDOSettings(params));
  }, [params, dispatch]);

  const columns = [
    {
      title: '',
      key: 'sort',
      width: '3%',
      render: () => <CustomIcon iconName="sort" />
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <>
          <Link to={`/p/credit_card_redemption_settings/cash-credit/show/${record.id}`}>
            <TableMainText>{record.name}</TableMainText>
            {record?.description && (
              <TableSecondaryText>{`${record?.description}`}</TableSecondaryText>
            )}
          </Link>
        </>
      )
    },
    {
      title: 'LRS Code',
      dataIndex: 'lrs_reward_code',
      key: 'lrs_reward_code',
      render: (_, record) => record.lrs_reward_code
    },
    {
      title: 'Conv. Rate',
      dataIndex: 'conversion_rate',
      key: 'conversion_rate',
      render: (_, record) => record.conversion_rate
    },
    {
      title: 'Min Points',
      dataIndex: 'min_redeem',
      key: 'min_redeem',
      render: (_, record) => record.min_redeem
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (_, record) => <TableTimestamp timestamp={record['start_date']} />
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (_, record) => <TableTimestamp timestamp={record['end_date']} />
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <TableActions
          testId="cash-credit-action-menu"
          actions={[
            {
              title: 'View Details',
              onClick: () => {
                navigate(`/p/credit_card_redemption_settings/cash-credit/show/${record.id}`);
              }
            },
            {
              title: 'Edit',
              onClick: () => {
                setIsEdit(true);
                setSelectedCashCredit(record.id);
                setModalOpen(true);
              }
            },
            {
              title: 'Delete',
              onClick: () =>
                confirm({
                  title: 'Do you want to delete this item?',
                  onOk() {
                    dispatch(deleteBDOSetting(record.id));
                  }
                })
            }
          ]}
        />
      ),
      align: 'right' as 'right'
    }
  ];

  return (
    <>
      <BDOSettingsCardComponents
        cardTitle="Cash Credit Lists"
        data={bdoSettings}
        columns={columns}
        mutate={() => dispatch(getBDOSettings(params))}
        params={params}
        paramsDispatch={paramsDispatch}
        loading={loading}
        onAddNewClick={() => {
          dispatch(clearBDOSettings());
          setIsEdit(false);
          setModalOpen(true);
          setSelectedCashCredit(null);
        }}
      />
      <BDOSettingAddEditModal
        modalOpen={modalOpen}
        closeModal={onCloseModal}
        title={'Cash Credit Program Details'}
        selectedBDOSetting={selectedCashCredit}
        isEdit={isEdit}
        BDOSettingType={'CashCredit'}
        cardItemType={'cash_credit'}
      />
    </>
  );
};

export default CashCreditLists;
